/**
 * PhoneField — a country-code phone input with live formatting (Typeform/Jotform
 * style). Defaults to US; national numbers are capped and formatted per country
 * (US/CA → "(555) 123-4567") so attendees can only enter a well-formed number.
 *
 * Emits a single string the rest of the app already understands:
 *   known country → "+1 (555) 123-4567"   (dial + a space + formatted national)
 *   "Other"       → "+<digits>"           (no space — disambiguates on parse)
 * normalizePhone()/phoneClaimKey() read the last 10 digits, so this stays
 * compatible with the phone-lookup + dedup flow.
 */
import React, { useMemo } from 'react';

const COUNTRIES = [
  { code: 'US', flag: '🇺🇸', dial: '+1',   len: 10, name: 'United States' },
  { code: 'IN', flag: '🇮🇳', dial: '+91',  len: 10, name: 'India' },
  { code: 'CA', flag: '🇨🇦', dial: '+1',   len: 10, name: 'Canada' },
  { code: 'GB', flag: '🇬🇧', dial: '+44',  len: 10, name: 'United Kingdom' },
  { code: 'AU', flag: '🇦🇺', dial: '+61',  len: 9,  name: 'Australia' },
  { code: 'AE', flag: '🇦🇪', dial: '+971', len: 9,  name: 'UAE' },
  { code: 'XX', flag: '🌐', dial: '+',    len: 15, name: 'Other' },
];
const DEFAULT = COUNTRIES[0];
const KNOWN = COUNTRIES.filter((c) => c.code !== 'XX').sort((a, b) => b.dial.length - a.dial.length);
const OTHER = COUNTRIES.find((c) => c.code === 'XX');

const onlyDigits = (s) => String(s || '').replace(/\D/g, '');

/** Parse a stored value back into { country, national-digits }. */
function parseValue(value) {
  const s = String(value || '').trim();
  if (!s) return { country: DEFAULT, national: '' };
  if (s.startsWith('+')) {
    for (const c of KNOWN) {
      if (s.startsWith(`${c.dial} `)) {           // known countries always store a space
        return { country: c, national: onlyDigits(s.slice(c.dial.length)).slice(-c.len) };
      }
    }
    return { country: OTHER, national: onlyDigits(s.slice(1)).slice(0, OTHER.len) };
  }
  const d = onlyDigits(s);
  return { country: DEFAULT, national: d.slice(-DEFAULT.len) };
}

/** Format the national digits for display. */
function formatNational(digits, country) {
  const d = digits.slice(0, country.len || 15);
  if (country.dial === '+1') { // North American grouping
    const a = d.slice(0, 3); const b = d.slice(3, 6); const c = d.slice(6, 10);
    if (d.length > 6) return `(${a}) ${b}-${c}`;
    if (d.length > 3) return `(${a}) ${b}`;
    if (d.length > 0) return `(${a}`;
    return '';
  }
  return d.replace(/(\d{3})(?=\d)/g, '$1 ').trim();
}

/** Recombine into the stored string. */
function combine(country, national) {
  if (!national) return '';
  if (country.code === 'XX') return `+${national}`;
  return `${country.dial} ${formatNational(national, country)}`;
}

export default function PhoneField({ value, onChange, onBlur, error, disabled }) {
  const { country, national } = useMemo(() => parseValue(value), [value]);

  const changeCountry = (code) => {
    const c = COUNTRIES.find((x) => x.code === code) || DEFAULT;
    onChange(combine(c, national.slice(0, c.len || 15)));
  };
  const changeNumber = (raw) => {
    const digits = onlyDigits(raw).slice(0, country.len || 15);
    onChange(combine(country, digits));
  };

  return (
    <div className={`evf-phone${error ? ' has-error' : ''}`}>
      <select className="evf-phone__cc" value={country.code} disabled={disabled}
        onChange={(e) => changeCountry(e.target.value)} aria-label="Country code">
        {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.flag} {c.dial}</option>)}
      </select>
      <input
        className="evf-input evf-phone__num" type="tel" inputMode="tel" autoComplete="tel"
        disabled={disabled} aria-invalid={error ? true : undefined}
        placeholder={country.dial === '+1' ? '(555) 123-4567' : 'Phone number'}
        value={formatNational(national, country)}
        onChange={(e) => changeNumber(e.target.value)}
        onBlur={onBlur}
      />
    </div>
  );
}
